import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Breadcrumb } from "../components/ui/breadcrumb";
import { Button } from "../components/ui/button";
import { Icons } from "../components/ui/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { Label } from "../components/ui/label";
import AutoSuggestion from "../components/auto-suggestion";
import { Item } from "./AddActivity";
import {
  Table,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableHeaderRow,
  TableRow,
  ResizableTableColumnHeader,
} from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

import QuotationPreview from "../components/quotation-preview";
import {
  Site,
  createQuotation,
  updateQuotation,
  getQuotation,
  getInvoice,
  CreateQuotationPayload,
  CreateQuotationItem,
  getInventories,
  getBankDetails,
  BankDetail,
  getCustomers,
  Customer,
  CustomerSite,
} from "../lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { format, addDays, addWeeks, addMonths } from "date-fns";
import { CalendarDatePicker } from "../components/ui/calendar-date-picker";
import { cn, getBackendErrorMessage } from "../lib/utils";
import { toast } from "sonner";
import { Columns, Plus, Trash2, GripVertical, Eye } from "lucide-react";
import { Modal } from "../components/ui/modal";
import { Checkbox } from "../components/ui/checkbox";
import { useIsMobile } from "../hooks/use-is-mobile";
import MobileFlowLayout, {
  MOBILE_FIELD_CLASS,
  MobileFlowCard,
  MobileFlowField,
} from "../components/mobile/mobile-flow-layout";
import MobileSelectField from "../components/mobile/mobile-select-field";
import MobileLineItemsEditor from "../components/mobile/mobile-line-items-editor";
import MobileDocumentReview from "../components/mobile/mobile-document-review";
import {
  ColumnDef,
  getDefaultQuotationColumns,
  evaluateFormula,
  getLineTotalFormulaColumn,
  nextColumnId,
  getFormulaEligibleColumns,
  normalizeFormulaToIds,
  getDefaultColumnWidth,
  getInitialColumnWidths,
} from "../lib/quote-invoice-columns";

const CORE_QUOTATION_COLUMN_IDS = [
  "sno",
  "description",
  "unit",
  "qty",
  "unitPrice",
  "total",
];

const MOBILE_STEPS = [
  { id: "details", label: "Details" },
  { id: "items", label: "Items" },
  { id: "extras", label: "Notes & bank" },
  { id: "review", label: "Review" },
];

/** Single string for description dropdown: "Description" or "Description 6". */
function descriptionWithLength(
  description?: string,
  length?: string | number,
): string {
  const d = (description ?? "").trim();
  const hasLength =
    length != null && length !== "" && String(length) !== "undefined";
  return hasLength ? `${d} ${length}`.trim() : d;
}

/** Value sent to API: combine base description with length, without a unit suffix. */
function serializeItemDescriptionForApi(
  description?: string,
  length?: string | number,
): string {
  const d = (description ?? "").trim();
  const hasLength =
    length != null && length !== "" && String(length) !== "undefined";
  if (!hasLength) return d;
  return `${d} ${length}`.trim();
}

interface QuotationItem extends Item {
  unit: string;
  unit_price: number;
  /** Custom column values (columnId -> string | number). */
  data?: Record<string, string | number>;
}

const CreateQuotation: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [mobileStepIndex, setMobileStepIndex] = useState(0);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null,
  );
  const [selectedSite, setSelectedSite] = useState<Site | CustomerSite | null>(
    null,
  );
  const [issueDate, setIssueDate] = useState<Date | null>(new Date());
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [items, setItems] = useState<QuotationItem[]>([]);
  const [inventoryItems, setInventoryItems] = useState<Item[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  /** Customers with sites loaded for combined Customer/Site autosuggestion */
  const [customersWithSites, setCustomersWithSites] = useState<Customer[]>([]);
  const [currency, setCurrency] = useState("TZS");
  const [isVatIncluded, setIsVatIncluded] = useState(false);
  const [showIsVatExempted, setShowIsVatExempted] = useState(false);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [reference, setReference] = useState("");
  const [description, setDescription] = useState(
    "We are pleased to quote you for the following",
  );
  const [bankDetails, setBankDetails] = useState<BankDetail[]>([]);
  const [selectedBankId, setSelectedBankId] = useState<string | undefined>(
    undefined,
  );
  const location = useLocation();
  const quotationIdFromState = location.state?.quotationId as
    | string
    | undefined;
  const duplicateQuotationIdFromState = location.state?.duplicateQuotationId as
    | string
    | undefined;
  const duplicateInvoiceIdFromState = location.state?.duplicateInvoiceId as
    | string
    | undefined;
  const [isEdit, setIsEdit] = useState(false);
  const [editingQuotationId, setEditingQuotationId] = useState<string | null>(
    null,
  );
  const [columns, setColumns] = useState<ColumnDef[]>(
    getDefaultQuotationColumns,
  );
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(() =>
    getDefaultQuotationColumns().reduce(
      (acc, col) => ({ ...acc, [col.id]: true }),
      {},
    ),
  );
  const [columnsConfigOpen, setColumnsConfigOpen] = useState(false);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() =>
    getInitialColumnWidths(getDefaultQuotationColumns()),
  );
  const [newColumnLabel, setNewColumnLabel] = useState("");
  const [newColumnType, setNewColumnType] = useState<ColumnDef["type"]>("text");
  const [newColumnFormula, setNewColumnFormula] = useState(
    "[Qty] * [Unit Price]",
  );
  const [formulaBuilderLeft, setFormulaBuilderLeft] = useState("");
  const [formulaBuilderOp, setFormulaBuilderOp] = useState("*");
  const [formulaBuilderRight, setFormulaBuilderRight] = useState("");
  const [editingColumnLabel, setEditingColumnLabel] = useState<
    Record<string, string>
  >({});
  const [draggedColumnIndex, setDraggedColumnIndex] = useState<number | null>(
    null,
  );
  /** Draft value while user is typing in a number cell (preserves "12." etc.) */
  const [editingNumberCell, setEditingNumberCell] = useState<{
    rowIndex: number;
    colId: string;
    draft: string;
  } | null>(null);

  const customersQuery = useQuery({
    queryKey: ["customers", { page: 1, limit: 1000 }],
    queryFn: async () => getCustomers(1, 1000),
  });

  const inventoriesQuery = useQuery({
    queryKey: ["inventories", { page: 1, limit: 1000, search: "" }],
    queryFn: async () => getInventories(1, 1000),
  });

  const bankDetailsQuery = useQuery({
    queryKey: ["bank-details", { page: 1, limit: 100 }],
    queryFn: async () => getBankDetails(1, 100),
  });

  const quotationQuery = useQuery({
    queryKey: ["quotations", quotationIdFromState],
    queryFn: async () => getQuotation(quotationIdFromState as string),
    enabled: !!quotationIdFromState,
  });

  const duplicateQuotationQuery = useQuery({
    queryKey: ["quotations", duplicateQuotationIdFromState],
    queryFn: async () => getQuotation(duplicateQuotationIdFromState as string),
    enabled: !!duplicateQuotationIdFromState && !quotationIdFromState,
  });

  const duplicateInvoiceQuery = useQuery({
    queryKey: ["invoices", duplicateInvoiceIdFromState],
    queryFn: async () => getInvoice(duplicateInvoiceIdFromState as string),
    enabled:
      !!duplicateInvoiceIdFromState &&
      !quotationIdFromState &&
      !duplicateQuotationIdFromState,
  });

  useEffect(() => {
    const e =
      customersQuery.error ??
      inventoriesQuery.error ??
      bankDetailsQuery.error ??
      quotationQuery.error ??
      duplicateQuotationQuery.error ??
      duplicateInvoiceQuery.error;
    if (!e) return;
    toast.error(getBackendErrorMessage(e, "Failed to fetch data"));
  }, [
    customersQuery.error,
    inventoriesQuery.error,
    bankDetailsQuery.error,
    quotationQuery.error,
    duplicateQuotationQuery.error,
    duplicateInvoiceQuery.error,
  ]);

  useEffect(() => {
    const customersRes = customersQuery.data?.data ?? [];
    setCustomersWithSites(customersRes);
  }, [customersQuery.data]);

  useEffect(() => {
    if (inventoriesQuery.data?.data) {
      setInventoryItems(inventoriesQuery.data.data as unknown as Item[]);
    }
  }, [inventoriesQuery.data]);

  useEffect(() => {
    if (bankDetailsQuery.data?.data) {
      setBankDetails(bankDetailsQuery.data.data);
    }
  }, [bankDetailsQuery.data]);

  const getRowForFormula = (
    item: QuotationItem,
    index: number,
  ): Record<string, number | string | undefined> => {
    const qty =
      Number(
        item.rental_quantity ?? (item as { quantity?: number }).quantity ?? 0,
      ) || 0;
    const unitPrice = Number(item.unit_price ?? 0) || 0;
    const lineTotal = qty * unitPrice;
    const discountType = String(
      (item.data?.discountType as string) ?? "None",
    ).trim();
    const discountVal = Number(item.data?.discount) || 0;
    const discountAmount =
      discountType === "Fixed"
        ? discountVal
        : discountType === "Percentage"
          ? Math.round(((lineTotal * discountVal) / 100) * 100) / 100
          : 0;
    return {
      ...(item.data ?? {}),
      sno: index + 1,
      description: item.description ?? "",
      unit: item.unit ?? "",
      qty,
      unitPrice,
      discountAmount,
    };
  };

  const getCellValue = (
    item: QuotationItem,
    colId: string,
  ): string | number => {
    if (colId === "sno") return 0;
    if (colId === "description") return item.description ?? "";
    if (colId === "unit") return item.unit ?? "";
    if (colId === "qty")
      return (
        item.rental_quantity ?? (item as { quantity?: number }).quantity ?? 0
      );
    if (colId === "unitPrice") return item.unit_price ?? 0;
    const v = item.data?.[colId];
    return v !== undefined && v !== "" ? v : "";
  };

  const setCellValue = (
    index: number,
    colId: string,
    value: string | number,
  ) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        if (colId === "description")
          return { ...item, description: String(value) };
        if (colId === "unit") return { ...item, unit: String(value) };
        if (colId === "qty")
          return { ...item, rental_quantity: Number(value) || 0 };
        if (colId === "unitPrice")
          return { ...item, unit_price: Number(value) || 0 };
        const nextData = { ...(item.data ?? {}), [colId]: value };
        return { ...item, data: nextData };
      }),
    );
  };

  const handleDescriptionSelect = (index: number, value: string) => {
    const selectedInv = inventoryItems.find(
      (inv) => descriptionWithLength(inv.description, inv.length) === value,
    );
    const invItemCode = selectedInv
      ? ((selectedInv as { item_code?: string; itemCode?: string }).item_code ??
        (selectedInv as { item_code?: string; itemCode?: string }).itemCode)
      : undefined;
    setItems(
      items.map((selected, idx) =>
        idx === index
          ? {
              ...selected,
              item_code: invItemCode ?? `custom-${Date.now()}`,
              description: selectedInv?.description ?? value,
              length: selectedInv?.length ?? undefined,
              unit_price: selectedInv
                ? (selectedInv.negotiated_hire_week_pc || 0) * 4
                : selected.unit_price,
              add_item_type: selectedInv ? "inventory" : "custom",
            }
          : selected,
      ),
    );
  };

  const handleDescriptionBlur = (index: number, value: string) => {
    if (!value) return;
    const selectedInv = inventoryItems.find(
      (inv) => descriptionWithLength(inv.description, inv.length) === value,
    );
    if (selectedInv) {
      const invItemCode =
        (selectedInv as { item_code?: string; itemCode?: string }).item_code ??
        (selectedInv as { item_code?: string; itemCode?: string }).itemCode;
      setItems((prev) =>
        prev.map((selected, idx) =>
          idx === index
            ? {
                ...selected,
                item_code: invItemCode ?? `custom-${Date.now()}`,
                description: selectedInv.description ?? value,
                length: selectedInv.length,
                add_item_type: "inventory",
              }
            : selected,
        ),
      );
    } else {
      setItems((prev) =>
        prev.map((selected, idx) =>
          idx === index
            ? {
                ...selected,
                item_code: `custom-${Date.now()}`,
                description: value,
                length: undefined,
                add_item_type: "custom",
              }
            : selected,
        ),
      );
    }
  };

  /** Keeps a draft string while typing so values like "12." are not normalized mid-edit. */
  const getNumberCellInputProps = (
    index: number,
    colId: string,
    numVal: number,
  ) => {
    const isEditing =
      editingNumberCell?.rowIndex === index &&
      editingNumberCell?.colId === colId;
    return {
      type: "text",
      inputMode: "decimal" as const,
      value: isEditing
        ? editingNumberCell!.draft
        : numVal === 0
          ? ""
          : String(numVal),
      onFocus: () =>
        setEditingNumberCell({
          rowIndex: index,
          colId,
          draft: numVal === 0 ? "" : String(numVal),
        }),
      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/,/g, "").trim();
        const parsed =
          raw === "" || raw === "-" || raw === "." ? 0 : Number(raw);
        setCellValue(index, colId, Number.isFinite(parsed) ? parsed : 0);
        setEditingNumberCell(null);
      },
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/,/g, "");
        if (!/^-?\d*\.?\d*$/.test(value)) return;
        setEditingNumberCell((prev) =>
          prev && prev.rowIndex === index && prev.colId === colId
            ? { ...prev, draft: value }
            : { rowIndex: index, colId, draft: value },
        );
      },
    };
  };

  const customerSiteValue = selectedCustomer
    ? selectedSite
      ? `${selectedCustomer.fullName} - ${selectedSite.name}`
      : selectedCustomer.fullName
    : "";

  const customerSiteSuggestions = customersWithSites.flatMap((c) => [
    c.fullName,
    ...(c.sites ?? []).map((s) => `${c.fullName} - ${s.name}`),
  ]);

  const handleCustomerSiteSelect = (value: string) => {
    const c = customersWithSites.find((x) => x.fullName === value);
    if (c) {
      setSelectedCustomer(c);
      setSelectedSite(null);
      return;
    }
    for (const cust of customersWithSites) {
      const site = (cust.sites ?? []).find(
        (s) => `${cust.fullName} - ${s.name}` === value,
      );
      if (site) {
        setSelectedCustomer(cust);
        setSelectedSite(site);
        return;
      }
    }
  };

  const openColumnsConfig = () => {
    setEditingColumnLabel(
      columns.reduce((acc, c) => ({ ...acc, [c.id]: c.label }), {}),
    );
    setColumnsConfigOpen(true);
  };

  const handleAddColumn = () => {
    const label = newColumnLabel.trim();
    if (!label) {
      toast.error("Column name is required");
      return;
    }
    const id = nextColumnId(columns);
    const def: ColumnDef = {
      id,
      label,
      type: newColumnType,
    };
    if (newColumnType === "formula") {
      def.formula = normalizeFormulaToIds(
        newColumnFormula.trim() || "[qty] * [unitPrice]",
        columns,
      );
    }
    setColumns((prev) => [...prev, def]);
    setColumnVisibility((prev) => ({ ...prev, [id]: true }));
    setNewColumnLabel("");
    setNewColumnType("text");
    setNewColumnFormula("[Qty] * [Unit Price]");
    setFormulaBuilderLeft("");
    setFormulaBuilderOp("*");
    setFormulaBuilderRight("");
  };

  const handleMoveColumn = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    setColumns((prev) => {
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      return next;
    });
  };

  const handleRenameColumn = (colId: string, newLabel: string) => {
    if (!newLabel.trim()) return;
    setColumns((prev) =>
      prev.map((c) => (c.id === colId ? { ...c, label: newLabel.trim() } : c)),
    );
    setEditingColumnLabel((prev) => ({ ...prev, [colId]: newLabel.trim() }));
  };

  const handleDeleteColumn = (colId: string) => {
    if (["description", "unit", "qty", "unitPrice"].includes(colId)) {
      toast.error(
        "Core columns (Description, Unit, Qty, Unit Price) cannot be removed",
      );
      return;
    }
    setColumns((prev) => prev.filter((c) => c.id !== colId));
    setColumnVisibility((prev) => {
      const next = { ...prev };
      delete next[colId];
      return next;
    });
  };

  // If a duplicateQuotationId is provided in navigation state, load it to prefill the create form
  useEffect(() => {
    const quote = duplicateQuotationQuery.data;
    if (!quote) return;
    try {
      // Prefill for creating a new quotation (do not switch to edit mode)
      setIsEdit(false);
      setEditingQuotationId(null);
      setIssueDate(new Date(quote.issueDate));
      setExpiryDate(quote.expiryDate ? new Date(quote.expiryDate) : null);
      setCurrency(quote.currencyType || "TZS");
      setIsVatIncluded(!!quote.isVatIncluded);
      setShowIsVatExempted(!!quote.showIsVatExempted);
      setTitle(quote.title ?? "");
      setNotes(quote.notes ?? "");
      setReference("");
      setDescription(
        quote.description ?? "We are pleased to quote you for the following",
      );
      setSelectedBankId(quote.bankDetailId || undefined);
      if (quote.customer) {
        setSelectedCustomer(quote.customer);
        setSelectedSite(quote.site ?? null);
      }
      const mappedItems = (quote.items || []).map(
        (it) =>
          ({
            item_code: (it as any).inventoryId
              ? `inv-${(it as any).inventoryId}`
              : `custom-${Date.now()}`,
            description: it.description || "",
            quantity: it.quantity || 0,
            unit: it.unit || "Month",
            unit_price: Number((it as any).unitPrice) || 0,
            rental_quantity: it.quantity || 0,
            id: (it as any).inventoryId || undefined,
            add_item_type: (it as any).inventoryId ? "inventory" : "custom",
            is_bundle: false,
            negotiated_hire_week_pc: 0,
            unit_weight: 0,
            data: it.data,
          }) as QuotationItem,
      );
      setItems(mappedItems);
      if (quote.columnConfig?.columns?.length) {
        setColumns(quote.columnConfig.columns);
        setColumnVisibility(
          quote.columnConfig.columnVisibility ??
            quote.columnConfig.columns.reduce(
              (acc, col) => ({ ...acc, [col.id]: true }),
              {},
            ),
        );
      }
    } catch (error) {
      toast.error(
        getBackendErrorMessage(
          error,
          "Failed to load quotation for duplication",
        ),
      );
    }
  }, [duplicateQuotationQuery.data]);

  // If duplicateInvoiceId is provided (e.g. from Invoices "Copy to new quotation"), load invoice and prefill
  useEffect(() => {
    const inv = duplicateInvoiceQuery.data;
    if (!inv) return;
    try {
      setIsEdit(false);
      setEditingQuotationId(null);
      setIssueDate(new Date(inv.issueDate));
      setExpiryDate(inv.dueDate ? new Date(inv.dueDate) : null);
      setCurrency(inv.currencyType || "TZS");
      setIsVatIncluded(!!inv.isVatIncluded);
      setShowIsVatExempted(!!inv.showIsVatExempted);
      setTitle(inv.title ?? "");
      setNotes(inv.notes ?? "");
      setReference("");
      setDescription(
        inv.description ?? "We are pleased to quote you for the following",
      );
      setSelectedBankId(inv.bankDetailId || undefined);
      if (inv.customer) {
        setSelectedCustomer(inv.customer);
        setSelectedSite(inv.site ?? null);
      }
      const mappedItems = (inv.items || []).map(
        (it) =>
          ({
            item_code: (it as any).inventoryId
              ? `inv-${(it as any).inventoryId}`
              : `custom-${Date.now()}`,
            description: it.description || "",
            quantity: it.quantity || 0,
            unit: it.unit || "Month",
            unit_price: Number((it as any).unitPrice) || 0,
            rental_quantity: it.quantity || 0,
            id: (it as any).inventoryId || undefined,
            add_item_type: (it as any).inventoryId ? "inventory" : "custom",
            is_bundle: false,
            negotiated_hire_week_pc: 0,
            unit_weight: 0,
            data: it.data,
          }) as QuotationItem,
      );
      setItems(mappedItems);
      if (inv.columnConfig?.columns?.length) {
        setColumns(inv.columnConfig.columns);
        setColumnVisibility(
          inv.columnConfig.columnVisibility ??
            inv.columnConfig.columns.reduce(
              (acc, col) => ({ ...acc, [col.id]: true }),
              {},
            ),
        );
      }
    } catch (error) {
      toast.error(
        getBackendErrorMessage(error, "Failed to load invoice for quotation"),
      );
    }
  }, [duplicateInvoiceQuery.data]);

  const breadcrumbItems = [
    { label: "Quotes", href: "/quotes" },
    { label: isEdit ? "Edit Quotation" : "Create Quotation" },
  ];

  // data is loaded via React Query above

  // If a quotationId is provided in navigation state, load it for editing
  useEffect(() => {
    const quote = quotationQuery.data;
    if (!quote) return;
    try {
      setIsEdit(true);
      setEditingQuotationId(quotationIdFromState as string);
      setIssueDate(new Date(quote.issueDate));
      setExpiryDate(quote.expiryDate ? new Date(quote.expiryDate) : null);
      setCurrency(quote.currencyType || "TZS");
      setIsVatIncluded(!!quote.isVatIncluded);
      setShowIsVatExempted(!!quote.showIsVatExempted);
      setTitle(quote.title ?? "");
      setNotes(quote.notes ?? "");
      setReference(quote.reference ?? "");
      setDescription(
        quote.description ?? "We are pleased to quote you for the following",
      );
      setSelectedBankId(quote.bankDetailId || undefined);
      if (quote.customer) {
        setSelectedCustomer(quote.customer);
        setSelectedSite(quote.site ?? null);
      }
      const mappedItems = (quote.items || []).map(
        (it) =>
          ({
            item_code: (it as any).inventoryId
              ? `inv-${(it as any).inventoryId}`
              : `custom-${Date.now()}`,
            description: it.description || "",
            quantity: it.quantity || 0,
            unit: it.unit || "Month",
            unit_price: Number((it as any).unitPrice) || 0,
            rental_quantity: it.quantity || 0,
            id: (it as any).inventoryId || undefined,
            add_item_type: (it as any).inventoryId ? "inventory" : "custom",
            is_bundle: false,
            negotiated_hire_week_pc: 0,
            unit_weight: 0,
            data: it.data,
          }) as QuotationItem,
      );
      setItems(mappedItems);
      if (quote.columnConfig?.columns?.length) {
        setColumns(quote.columnConfig.columns);
        setColumnVisibility(
          quote.columnConfig.columnVisibility ??
            quote.columnConfig.columns.reduce(
              (acc, col) => ({ ...acc, [col.id]: true }),
              {},
            ),
        );
      }
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to load quotation"));
    }
  }, [quotationQuery.data, quotationIdFromState]);

  const addNewItem = () => {
    const newItem: QuotationItem = {
      item_code: `custom-${Date.now()}`,
      description: "",
      quantity: 0,
      unit_weight: 0,
      unit: "Month",
      unit_price: 0,
      rental_quantity: 1,
      add_item_type: "custom",
      is_bundle: false,
      negotiated_hire_week_pc: 0,
    };
    setItems([...items, newItem]);
  };

  const saveQuotationMutation = useMutation({
    mutationFn: async (payload: CreateQuotationPayload) => {
      if (isEdit && editingQuotationId) {
        return updateQuotation(editingQuotationId, payload);
      }
      return createQuotation(payload);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["quotations"] }),
        queryClient.invalidateQueries({
          queryKey: ["quotations", "statistics"],
        }),
      ]);
    },
    onError: (error) => {
      toast.error(getBackendErrorMessage(error, "Failed to save quotation"));
    },
  });

  const loading = useMemo(() => {
    return (
      saveQuotationMutation.isPending ||
      customersQuery.isLoading ||
      inventoriesQuery.isLoading ||
      bankDetailsQuery.isLoading ||
      quotationQuery.isLoading ||
      duplicateQuotationQuery.isLoading ||
      duplicateInvoiceQuery.isLoading
    );
  }, [
    saveQuotationMutation.isPending,
    customersQuery.isLoading,
    inventoriesQuery.isLoading,
    bankDetailsQuery.isLoading,
    quotationQuery.isLoading,
    duplicateQuotationQuery.isLoading,
    duplicateInvoiceQuery.isLoading,
  ]);

  const handleSave = async () => {
    if (!selectedCustomer || !issueDate || !items || items.length === 0) {
      toast.error(
        "Please fill all required fields (Customer, Issue date, and at least one item)",
      );
      return;
    }

    try {
      const payloadItems: CreateQuotationItem[] = items.map((item, i) => {
        const start = issueDate!;
        let end = issueDate!;
        const u = (item.unit || "").toLowerCase();
        if (u === "day") end = addDays(start, 1);
        else if (u === "week") end = addWeeks(start, 1);
        else if (u === "month") end = addMonths(start, 1);
        else end = addMonths(start, 1);

        const customData = columns
          .filter(
            (c) =>
              !CORE_QUOTATION_COLUMN_IDS.includes(c.id) && c.type !== "formula",
          )
          .reduce<Record<string, string | number>>((acc, c) => {
            const v = getCellValue(item, c.id);
            if (v !== "" && v !== undefined) acc[c.id] = v as string | number;
            return acc;
          }, {});

        return {
          description: serializeItemDescriptionForApi(
            item.description,
            item.length,
          ),
          quantity: item.rental_quantity || 0,
          unit: item.unit,
          unitPrice: item.unit_price,
          amount: getRowAmount(item, i),
          startDate: format(start, "yyyy-MM-dd"),
          endDate: format(end, "yyyy-MM-dd"),
          inventoryId: item.id || undefined,
          ...(Object.keys(customData).length > 0 ? { data: customData } : {}),
        };
      });

      const payload: CreateQuotationPayload = {
        customerId: selectedCustomer.id,
        ...(selectedSite ? { siteId: selectedSite.id } : {}),
        issueDate: format(issueDate!, "yyyy-MM-dd"),
        expiryDate:
          expiryDate != null ? format(expiryDate, "yyyy-MM-dd") : null,
        currencyType: currency,
        amount: calculateTotal(),
        isVatIncluded,
        showIsVatExempted,
        title: title || undefined,
        notes: notes || undefined,
        reference: reference || undefined,
        description: description || undefined,
        bankDetailId: selectedBankId ?? null,
        items: payloadItems,
        columnConfig: { columns, columnVisibility },
      };

      await saveQuotationMutation.mutateAsync(payload);

      navigate("/quotes");
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to save quotation"));
    }
  };

  const getRowAmount = (item: QuotationItem, index: number): number => {
    let amount: number;
    const formulaCol = getLineTotalFormulaColumn(columns);
    if (formulaCol?.formula) {
      amount = evaluateFormula(
        formulaCol.formula,
        getRowForFormula(item, index),
        columns,
      );
    } else {
      const qty =
        item.rental_quantity ?? (item as { quantity?: number }).quantity ?? 0;
      amount = qty * (item.unit_price ?? 0);
    }
    return Math.round(amount * 100) / 100;
  };

  const calculateSubTotal = () => {
    const sum = (items || []).reduce(
      (acc, item, i) => acc + getRowAmount(item, i),
      0,
    );
    return Math.round(sum * 100) / 100;
  };

  const calculateVAT = () => {
    if (showIsVatExempted || !isVatIncluded) return 0;
    return Math.round(calculateSubTotal() * 0.18 * 100) / 100;
  };

  const calculateTotal = () => {
    return Math.round((calculateSubTotal() + calculateVAT()) * 100) / 100;
  };

  const selectedBank = bankDetails.find((b) => b.id === selectedBankId);

  const previewPanel = (
    <QuotationPreview
      customer={selectedCustomer ?? undefined}
      items={items || []}
      issueDate={issueDate || undefined}
      expiryDate={expiryDate || undefined}
      title={title || undefined}
      notes={notes || undefined}
      reference={reference || undefined}
      description={description || undefined}
      onClose={() => setShowPreview(false)}
      isOpen={showPreview}
      isVatIncluded={isVatIncluded}
      showIsVatExempted={showIsVatExempted}
      currency={currency}
      bankDetail={selectedBank}
      columns={columns}
      columnVisibility={columnVisibility}
    />
  );

  const columnsConfigModal = (
    <Modal
      isOpen={columnsConfigOpen}
      onClose={() => setColumnsConfigOpen(false)}
      title="Column configuration"
      description="Reorder by dragging, toggle visibility, rename, or add columns."
      className="max-w-lg"
    >
      <div className="p-6 space-y-6">
        {/* Add column */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-gray-900">Add column</h3>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[120px] space-y-1">
              <Label className="text-xs">Name</Label>
              <Input
                value={newColumnLabel}
                onChange={(e) => setNewColumnLabel(e.target.value)}
                placeholder="e.g. Discount"
                className="h-8"
              />
            </div>
            <div className="w-28 space-y-1">
              <Label className="text-xs">Type</Label>
              <Select
                value={newColumnType}
                onValueChange={(v) => setNewColumnType(v as ColumnDef["type"])}
              >
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="formula">Formula</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              size="sm"
              className="h-8"
              onClick={handleAddColumn}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
          {newColumnType === "formula" && (
            <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50/50 p-3">
              <p className="text-xs text-gray-600">
                Use column names in brackets. Operators: + − × ÷
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Select
                  value={formulaBuilderLeft}
                  onValueChange={setFormulaBuilderLeft}
                >
                  <SelectTrigger className="h-8 w-32">
                    <SelectValue placeholder="Column" />
                  </SelectTrigger>
                  <SelectContent>
                    {getFormulaEligibleColumns(columns).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={formulaBuilderOp}
                  onValueChange={setFormulaBuilderOp}
                >
                  <SelectTrigger className="h-8 w-16">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="*">×</SelectItem>
                    <SelectItem value="/">÷</SelectItem>
                    <SelectItem value="+">+</SelectItem>
                    <SelectItem value="-">−</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={formulaBuilderRight}
                  onValueChange={setFormulaBuilderRight}
                >
                  <SelectTrigger className="h-8 w-32">
                    <SelectValue placeholder="Column" />
                  </SelectTrigger>
                  <SelectContent>
                    {getFormulaEligibleColumns(columns).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-8"
                  onClick={() => {
                    if (!formulaBuilderLeft || !formulaBuilderRight) return;
                    const leftCol = columns.find(
                      (c) => c.id === formulaBuilderLeft,
                    );
                    const rightCol = columns.find(
                      (c) => c.id === formulaBuilderRight,
                    );
                    if (leftCol && rightCol) {
                      setNewColumnFormula(
                        `[${leftCol.label}] ${formulaBuilderOp} [${rightCol.label}]`,
                      );
                    }
                  }}
                >
                  Use this
                </Button>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">
                  Formula (or edit after using builder)
                </Label>
                <Input
                  value={newColumnFormula}
                  onChange={(e) => setNewColumnFormula(e.target.value)}
                  placeholder="e.g. [Qty] * [Unit Price]"
                  className="h-8 bg-white"
                />
              </div>
            </div>
          )}
        </div>

        {/* Column list: drag, visibility, rename, delete */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-gray-900">
            Columns (drag to reorder)
          </h3>
          <div className="border rounded-md divide-y max-h-64 overflow-y-auto">
            {columns.map((col, index) => (
              <div
                key={col.id}
                draggable
                onDragStart={(e) => {
                  setDraggedColumnIndex(index);
                  e.dataTransfer.setData("text/plain", String(index));
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const fromIndex = Number(
                    e.dataTransfer.getData("text/plain"),
                  );
                  if (Number.isFinite(fromIndex) && fromIndex !== index) {
                    handleMoveColumn(fromIndex, index);
                  }
                }}
                onDragEnd={() => setDraggedColumnIndex(null)}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 bg-white hover:bg-gray-50",
                  draggedColumnIndex === index && "opacity-50",
                )}
              >
                <div
                  className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
                  title="Drag to reorder"
                >
                  <GripVertical className="h-4 w-4" />
                </div>
                <Checkbox
                  checked={columnVisibility[col.id] !== false}
                  onCheckedChange={(checked) =>
                    setColumnVisibility((prev) => ({
                      ...prev,
                      [col.id]: checked !== false,
                    }))
                  }
                  aria-label={`Show ${col.label}`}
                />
                <Input
                  className="flex-1 h-8 min-w-0"
                  value={editingColumnLabel[col.id] ?? col.label}
                  onChange={(e) =>
                    setEditingColumnLabel((prev) => ({
                      ...prev,
                      [col.id]: e.target.value,
                    }))
                  }
                  onBlur={(e) => handleRenameColumn(col.id, e.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
                  onClick={() => handleDeleteColumn(col.id)}
                  title="Remove column"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );

  if (isMobile) {
    const mobileStepId = MOBILE_STEPS[mobileStepIndex].id;
    const isLastMobileStep = mobileStepIndex === MOBILE_STEPS.length - 1;
    const isMobileNextDisabled =
      (mobileStepId === "details" && (!selectedCustomer || !issueDate)) ||
      (mobileStepId === "items" && items.length === 0);

    const renderMobileItemField = (
      col: ColumnDef,
      item: QuotationItem,
      index: number,
    ) => {
      if (col.id === "description") {
        return (
          <AutoSuggestion
            value={descriptionWithLength(item.description, item.length)}
            suggestions={inventoryItems.map((inv) =>
              descriptionWithLength(inv.description, inv.length),
            )}
            placeholder="Search or enter custom"
            className={MOBILE_FIELD_CLASS}
            onSelect={(value) => handleDescriptionSelect(index, value)}
            onBlur={(value) => handleDescriptionBlur(index, value)}
          />
        );
      }
      if (col.type === "number") {
        const numVal = Number(getCellValue(item, col.id)) || 0;
        return (
          <Input
            className={MOBILE_FIELD_CLASS}
            {...getNumberCellInputProps(index, col.id, numVal)}
          />
        );
      }
      if (col.type === "select") {
        const options = col.options ?? ["Day", "Week", "Month"];
        const cellVal = String(getCellValue(item, col.id));
        return (
          <MobileSelectField
            value={cellVal || options[0] || ""}
            options={options.map((opt) => ({ label: opt, value: opt }))}
            onChange={(val) => setCellValue(index, col.id, val)}
          />
        );
      }
      return (
        <Input
          className={MOBILE_FIELD_CLASS}
          value={String(getCellValue(item, col.id))}
          onChange={(e) => setCellValue(index, col.id, e.target.value)}
        />
      );
    };

    return (
      <>
        {previewPanel}
        {columnsConfigModal}
        <MobileFlowLayout
          title={isEdit ? "Edit Quotation" : "Create Quotation"}
          steps={MOBILE_STEPS}
          currentStepIndex={mobileStepIndex}
          onClose={() => navigate("/quotes")}
          actions={[
            {
              label: "Preview",
              icon: Eye,
              onClick: () => setShowPreview(true),
            },
            {
              label: "Configure columns",
              icon: Columns,
              onClick: openColumnsConfig,
            },
          ]}
          onBack={
            mobileStepIndex > 0
              ? () => setMobileStepIndex((i) => i - 1)
              : undefined
          }
          primaryAction={
            isLastMobileStep
              ? {
                  label: loading
                    ? isEdit
                      ? "Updating..."
                      : "Saving..."
                    : isEdit
                      ? "Edit Quotation"
                      : "Create Quotation",
                  onClick: handleSave,
                  disabled: loading,
                  loading: saveQuotationMutation.isPending,
                }
              : {
                  label: "Next",
                  onClick: () => setMobileStepIndex((i) => i + 1),
                  disabled: isMobileNextDisabled,
                }
          }
        >
          {mobileStepId === "details" && (
            <>
              <MobileFlowCard title="Customer">
                <MobileFlowField
                  label={
                    <>
                      Customer / Site <span className="text-red-500">*</span>
                    </>
                  }
                >
                  <AutoSuggestion
                    value={customerSiteValue}
                    suggestions={customerSiteSuggestions}
                    placeholder="Search customer or customer - site..."
                    className={MOBILE_FIELD_CLASS}
                    onSelect={handleCustomerSiteSelect}
                  />
                </MobileFlowField>
              </MobileFlowCard>

              <MobileFlowCard title="Dates & currency">
                <div className="space-y-4">
                  <MobileFlowField label="Currency">
                    <MobileSelectField
                      value={currency}
                      options={[
                        { label: "TZS", value: "TZS" },
                        { label: "USD", value: "USD" },
                      ]}
                      onChange={setCurrency}
                    />
                  </MobileFlowField>
                  <MobileFlowField
                    label={
                      <>
                        Issue Date <span className="text-red-500">*</span>
                      </>
                    }
                  >
                    <CalendarDatePicker
                      selected={issueDate}
                      onChange={setIssueDate}
                      className={cn("w-full", MOBILE_FIELD_CLASS)}
                    />
                  </MobileFlowField>
                  <MobileFlowField label="Expiry Date">
                    <div className="flex items-center gap-2">
                      <CalendarDatePicker
                        selected={expiryDate}
                        onChange={setExpiryDate}
                        className={cn(
                          "w-full min-w-0 flex-1",
                          MOBILE_FIELD_CLASS,
                        )}
                      />
                      {expiryDate && (
                        <button
                          type="button"
                          onClick={() => setExpiryDate(null)}
                          className="h-11 shrink-0 rounded-xl border-0 bg-gray-50 px-3 text-sm font-medium text-gray-600 active:bg-gray-100"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </MobileFlowField>
                </div>
              </MobileFlowCard>

              <MobileFlowCard title="VAT">
                <div className="divide-y divide-gray-100">
                  <div className="flex items-center justify-between gap-3 pb-3">
                    <Label
                      htmlFor="vat-mode-mobile"
                      className="text-sm font-medium"
                    >
                      Include VAT (18%)
                    </Label>
                    <Switch
                      id="vat-mode-mobile"
                      checked={isVatIncluded}
                      onCheckedChange={setIsVatIncluded}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 pt-3">
                    <Label
                      htmlFor="vat-exempt-mobile"
                      className="text-sm font-medium"
                    >
                      Show VAT Exempt
                    </Label>
                    <Switch
                      id="vat-exempt-mobile"
                      checked={showIsVatExempted}
                      onCheckedChange={setShowIsVatExempted}
                    />
                  </div>
                </div>
              </MobileFlowCard>

              <MobileFlowCard title="Title & reference" description="Optional">
                <div className="space-y-4">
                  <MobileFlowField label="Title" htmlFor="title-mobile">
                    <Input
                      id="title-mobile"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Optional title"
                      className={MOBILE_FIELD_CLASS}
                    />
                  </MobileFlowField>
                  <MobileFlowField label="Reference" htmlFor="reference-mobile">
                    <Input
                      id="reference-mobile"
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      placeholder="e.g. 20260001"
                      className={MOBILE_FIELD_CLASS}
                    />
                  </MobileFlowField>
                  <MobileFlowField
                    label="Description"
                    htmlFor="description-mobile"
                    hint="Introductory text shown above the items table."
                  >
                    <Input
                      id="description-mobile"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="We are pleased to quote you for the following"
                      className={MOBILE_FIELD_CLASS}
                    />
                  </MobileFlowField>
                </div>
              </MobileFlowCard>
            </>
          )}

          {mobileStepId === "items" && (
            <MobileLineItemsEditor
              title="Quote Items"
              items={items}
              columns={columns}
              columnVisibility={columnVisibility}
              getItemKey={(_, index) => index}
              renderField={renderMobileItemField}
              getFormulaValue={(col, item, index) =>
                evaluateFormula(
                  col.formula ?? "",
                  getRowForFormula(item, index),
                  columns,
                )
              }
              onAdd={addNewItem}
              onRemove={(index) =>
                setItems((prev) => prev.filter((_, idx) => idx !== index))
              }
              onConfigureColumns={openColumnsConfig}
              currency={currency}
              subTotal={calculateSubTotal()}
              vat={calculateVAT()}
              total={calculateTotal()}
            />
          )}

          {mobileStepId === "extras" && (
            <>
              <MobileFlowCard title="Notes" description="Optional">
                <Textarea
                  id="notes-mobile"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional notes (line breaks and spaces will appear in preview)"
                  rows={5}
                  className="min-h-[120px] resize-y rounded-xl"
                />
              </MobileFlowCard>
              <MobileFlowCard title="Beneficiary bank" description="Optional">
                <MobileSelectField
                  value={selectedBankId ?? "__none__"}
                  options={[
                    { label: "None", value: "__none__" },
                    ...bankDetails.map((bd) => ({
                      label: `${bd.bankName} — ${bd.accountNumber}`,
                      value: bd.id,
                    })),
                  ]}
                  onChange={(val) =>
                    setSelectedBankId(val === "__none__" ? undefined : val)
                  }
                />
              </MobileFlowCard>
            </>
          )}

          {mobileStepId === "review" && (
            <MobileDocumentReview
              rows={[
                { label: "Customer", value: selectedCustomer?.fullName },
                { label: "Site", value: selectedSite?.name },
                {
                  label: "Issue date",
                  value: issueDate ? format(issueDate, "PPP") : undefined,
                },
                {
                  label: "Expiry date",
                  value: expiryDate ? format(expiryDate, "PPP") : undefined,
                },
                { label: "Title", value: title },
                { label: "Reference", value: reference },
                {
                  label: "Bank",
                  value: selectedBank
                    ? `${selectedBank.bankName} — ${selectedBank.accountNumber}`
                    : undefined,
                },
              ]}
              lines={items.map((item, index) => ({
                key: index,
                description:
                  descriptionWithLength(item.description, item.length) ||
                  `Item ${index + 1}`,
                detail: `${item.rental_quantity ?? 0} × ${(item.unit_price ?? 0).toLocaleString()}${item.unit ? ` / ${item.unit}` : ""}`,
                amount: getRowAmount(item, index),
              }))}
              currency={currency}
              subTotal={calculateSubTotal()}
              vat={calculateVAT()}
              total={calculateTotal()}
              onPreview={() => setShowPreview(true)}
            />
          )}
        </MobileFlowLayout>
      </>
    );
  }

  return (
    <>
      {previewPanel}

      <div className="flex h-full">
        <div className="relative flex flex-1 flex-col bg-white rounded-l-[12px] overflow-hidden border-l border-gray-100">
          <div className="border-b px-8 py-4">
            <div className="flex flex-wrap items-center justify-between">
              <div className="space-y-4">
                <Breadcrumb items={breadcrumbItems} />
                <h1 className="text-2xl font-bold font-jakarta">
                  {isEdit ? "Edit Quotation" : "Create Quotation"}
                </h1>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 text-sm flex items-center gap-1.5"
                  onClick={() => setShowPreview(true)}
                >
                  <Icons.Preview className="h-4 w-4" />
                  Preview
                </Button>
                <Button
                  type="button"
                  className="h-9 text-sm bg-primary-600 hover:bg-primary-700 text-white"
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading
                    ? isEdit
                      ? "Updating..."
                      : "Saving..."
                    : isEdit
                      ? "Edit Quotation"
                      : "Create Quotation"}
                </Button>
                <div className="h-4 w-px bg-gray-200 mx-2" />
                <Button
                  type="button"
                  variant="ghost"
                  className="h-9 w-9 p-0 text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                  onClick={() => {
                    navigate("/quotes");
                  }}
                >
                  <Icons.X className="h-4 w-4 cursor-pointer" />
                </Button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6">
            <div className="text-sm text-gray-800">
              <Accordion
                type="single"
                collapsible
                defaultValue="details"
                className="w-full space-y-3"
              >
                {/* Details — open by default */}
                <AccordionItem
                  value="details"
                  className="border rounded-lg bg-gray-50/50 overflow-hidden [&>div:last-child]:border-b-0"
                >
                  <AccordionTrigger className="px-4 py-4 hover:no-underline [&[data-state=open]]:border-b [&[data-state=open]]:border-gray-200">
                    <span className="text-base font-medium">Details</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 pt-0">
                    <div className="space-y-6 pt-2">
                      {/* Customer / Site — full width */}
                      <div className="space-y-2">
                        <Label htmlFor="customer-site" className="font-medium">
                          Customer / Site{" "}
                          <span className="text-red-500">*</span>
                        </Label>
                        <AutoSuggestion
                          value={customerSiteValue}
                          suggestions={customerSiteSuggestions}
                          placeholder="Search customer or customer - site..."
                          onSelect={handleCustomerSiteSelect}
                        />
                      </div>

                      {/* Currency & dates — one row on desktop */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <Label className="font-medium">Currency</Label>
                          <Select value={currency} onValueChange={setCurrency}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select currency" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="TZS">TZS</SelectItem>
                              <SelectItem value="USD">USD</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="font-medium">Issue Date</Label>
                          <CalendarDatePicker
                            selected={issueDate}
                            onChange={setIssueDate}
                          />
                        </div>
                        <div className="space-y-2 sm:col-span-2 md:col-span-1">
                          <Label className="font-medium">Expiry Date</Label>
                          <div className="flex items-center gap-2">
                            <CalendarDatePicker
                              selected={expiryDate}
                              onChange={setExpiryDate}
                            />
                            {expiryDate && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-gray-500 hover:text-red-600 hover:bg-red-50 shrink-0"
                                onClick={() => setExpiryDate(null)}
                              >
                                Clear
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* VAT */}
                      <div className="pt-2 border-t border-gray-100 flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Switch
                            id="vat-mode"
                            checked={isVatIncluded}
                            className={cn(
                              "h-6 w-11",
                              "bg-gray-200 data-[state=checked]:bg-primary-600",
                              "border-2 border-transparent",
                            )}
                            onCheckedChange={setIsVatIncluded}
                          />
                          <Label htmlFor="vat-mode" className="font-medium">
                            Include VAT (18%)
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            id="vat-exempt"
                            checked={showIsVatExempted}
                            className={cn(
                              "h-6 w-11",
                              "bg-gray-200 data-[state=checked]:bg-primary-600",
                              "border-2 border-transparent",
                            )}
                            onCheckedChange={setShowIsVatExempted}
                          />
                          <Label htmlFor="vat-exempt" className="font-medium">
                            Show VAT Exempt
                          </Label>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Title & Reference */}
                <AccordionItem
                  value="title"
                  className="border rounded-lg bg-gray-50/50 overflow-hidden [&>div:last-child]:border-b-0"
                >
                  <AccordionTrigger className="px-4 py-4 hover:no-underline [&[data-state=open]]:border-b [&[data-state=open]]:border-gray-200">
                    <span className="text-base font-medium">
                      Title & Reference (Optional)
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 pt-0">
                    <div className="pt-2 space-y-3">
                      <div>
                        <Label htmlFor="title" className="text-sm">
                          Title
                        </Label>
                        <Input
                          id="title"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          placeholder="Optional title"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="reference" className="text-sm">
                          Reference
                        </Label>
                        <Input
                          id="reference"
                          value={reference}
                          onChange={(e) => setReference(e.target.value)}
                          placeholder="e.g. 20260001"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Description */}
                <AccordionItem
                  value="description"
                  className="border rounded-lg bg-gray-50/50 overflow-hidden [&>div:last-child]:border-b-0"
                >
                  <AccordionTrigger className="px-4 py-4 hover:no-underline [&[data-state=open]]:border-b [&[data-state=open]]:border-gray-200">
                    <span className="text-base font-medium">
                      Description (Optional)
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 pt-0">
                    <div className="space-y-2 pt-2">
                      <Input
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="We are pleased to quote you for the following"
                      />
                      <p className="text-xs text-muted-foreground">
                        Introductory text shown above the items table.
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Quote items */}
                <AccordionItem
                  value="items"
                  className="border rounded-lg bg-gray-50/50 overflow-hidden [&>div:last-child]:border-b-0"
                >
                  <AccordionTrigger className="px-4 py-4 hover:no-underline [&[data-state=open]]:border-b [&[data-state=open]]:border-gray-200">
                    <span className="text-base font-medium">Quote Items</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 pt-0">
                    <div className="pt-2 flex flex-col gap-3">
                      <div className="flex flex-row items-center justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 gap-1.5"
                          onClick={openColumnsConfig}
                        >
                          <Columns className="h-4 w-4" />
                          Columns
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 text-primary-600 hover:bg-primary-50"
                          onClick={addNewItem}
                        >
                          <Icons.Plus className="h-4 w-4 mr-1" />
                          Add Item
                        </Button>
                      </div>
                      <Table
                        layout="fixed"
                        className="border-collapse [&_th]:border-r [&_td]:border-r [&_th]:border-gray-200 [&_td]:border-gray-200"
                      >
                        <TableHeader>
                          <TableHeaderRow>
                            {columns.map(
                              (col) =>
                                columnVisibility[col.id] !== false && (
                                  <ResizableTableColumnHeader
                                    key={col.id}
                                    columnId={col.id}
                                    width={
                                      columnWidths[col.id] ??
                                      getDefaultColumnWidth(col.id)
                                    }
                                    onResize={(id, w) =>
                                      setColumnWidths((prev) => ({
                                        ...prev,
                                        [id]: w,
                                      }))
                                    }
                                  >
                                    {col.label}
                                  </ResizableTableColumnHeader>
                                ),
                            )}
                            <TableColumnHeader className="w-20">
                              &nbsp;
                            </TableColumnHeader>
                          </TableHeaderRow>
                        </TableHeader>
                        <TableBody>
                          {items.map((item, index) => {
                            const row = getRowForFormula(item, index);
                            const getColWidth = (id: string) =>
                              columnWidths[id] ?? getDefaultColumnWidth(id);
                            return (
                              <TableRow key={index}>
                                {columns.map((col) => {
                                  if (columnVisibility[col.id] === false)
                                    return null;
                                  const cellWidth = getColWidth(col.id);
                                  const cellStyle = {
                                    width: cellWidth,
                                    minWidth: cellWidth,
                                    maxWidth: cellWidth,
                                  } as React.CSSProperties;
                                  if (col.type === "readonly") {
                                    return (
                                      <TableCell key={col.id} style={cellStyle}>
                                        {index + 1}
                                      </TableCell>
                                    );
                                  }
                                  if (col.type === "formula" && col.formula) {
                                    const val = evaluateFormula(
                                      col.formula,
                                      row,
                                      columns,
                                    );
                                    return (
                                      <TableCell key={col.id} style={cellStyle}>
                                        {Number.isFinite(val)
                                          ? val.toLocaleString()
                                          : ""}
                                      </TableCell>
                                    );
                                  }
                                  if (col.type === "text") {
                                    const isDescription =
                                      col.id === "description";
                                    return (
                                      <TableCell key={col.id} style={cellStyle}>
                                        {isDescription ? (
                                          <AutoSuggestion
                                            value={descriptionWithLength(
                                              item.description,
                                              item.length,
                                            )}
                                            suggestions={inventoryItems.map(
                                              (inv) =>
                                                descriptionWithLength(
                                                  inv.description,
                                                  inv.length,
                                                ),
                                            )}
                                            placeholder="Search or enter custom"
                                            onSelect={(value) =>
                                              handleDescriptionSelect(
                                                index,
                                                value,
                                              )
                                            }
                                            onBlur={(value) =>
                                              handleDescriptionBlur(
                                                index,
                                                value,
                                              )
                                            }
                                          />
                                        ) : (
                                          <Input
                                            className="h-7"
                                            value={String(
                                              getCellValue(item, col.id),
                                            )}
                                            onChange={(e) =>
                                              setCellValue(
                                                index,
                                                col.id,
                                                e.target.value,
                                              )
                                            }
                                          />
                                        )}
                                      </TableCell>
                                    );
                                  }
                                  if (col.type === "number") {
                                    const numVal =
                                      Number(getCellValue(item, col.id)) || 0;
                                    return (
                                      <TableCell key={col.id} style={cellStyle}>
                                        <Input
                                          className="h-7"
                                          {...getNumberCellInputProps(
                                            index,
                                            col.id,
                                            numVal,
                                          )}
                                        />
                                      </TableCell>
                                    );
                                  }
                                  if (col.type === "select") {
                                    const options = col.options ?? [
                                      "Day",
                                      "Week",
                                      "Month",
                                    ];
                                    const cellVal = String(
                                      getCellValue(item, col.id),
                                    );
                                    return (
                                      <TableCell key={col.id} style={cellStyle}>
                                        <Select
                                          value={cellVal || options[0] || ""}
                                          onValueChange={(val) =>
                                            setCellValue(index, col.id, val)
                                          }
                                        >
                                          <SelectTrigger className="h-7 w-24">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {options.map((opt) => (
                                              <SelectItem key={opt} value={opt}>
                                                {opt}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </TableCell>
                                    );
                                  }
                                  return null;
                                })}
                                <TableCell>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="text-gray-500 hover:text-red-600 hover:bg-red-50 h-8 px-2 shrink-0"
                                    onClick={() => {
                                      setItems((prev) =>
                                        prev.filter((_, idx) => idx !== index),
                                      );
                                    }}
                                  >
                                    <Icons.Delete className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>

                      {columnsConfigModal}

                      <div className="flex justify-end gap-4 mt-4">
                        <div className="text-right pr-2">
                          <div>
                            Sub Total:{" "}
                            <strong>
                              {currency} {calculateSubTotal().toLocaleString()}
                            </strong>
                          </div>
                          <div>
                            VAT:{" "}
                            <strong>
                              {currency} {calculateVAT().toLocaleString()}
                            </strong>
                          </div>
                          <div className="text-lg font-bold">
                            Total:{" "}
                            <strong>
                              {currency} {calculateTotal().toLocaleString()}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Notes */}
                <AccordionItem
                  value="notes"
                  className="border rounded-lg bg-gray-50/50 overflow-hidden [&>div:last-child]:border-b-0"
                >
                  <AccordionTrigger className="px-4 py-4 hover:no-underline [&[data-state=open]]:border-b [&[data-state=open]]:border-gray-200">
                    <span className="text-base font-medium">
                      Notes (Optional)
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 pt-0">
                    <div className="pt-2">
                      <Textarea
                        id="notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Optional notes (line breaks and spaces will appear in preview)"
                        rows={4}
                        className="min-h-[80px] resize-y"
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Beneficiary Bank */}
                <AccordionItem
                  value="beneficiary"
                  className="border rounded-lg bg-gray-50/50 overflow-hidden [&>div:last-child]:border-b-0"
                >
                  <AccordionTrigger className="px-4 py-4 hover:no-underline [&[data-state=open]]:border-b [&[data-state=open]]:border-gray-200">
                    <span className="text-base font-medium">
                      Beneficiary Bank (Optional)
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 pt-0">
                    <div className="pt-2">
                      <div className="flex items-center gap-2">
                        <Select
                          value={selectedBankId ?? "__none__"}
                          onValueChange={(val: string) =>
                            setSelectedBankId(
                              val === "__none__" ? undefined : val,
                            )
                          }
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Select bank" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">None</SelectItem>
                            {bankDetails.map((bd) => (
                              <SelectItem
                                key={bd.id}
                                value={bd.id}
                              >{`${bd.bankName} — ${bd.accountNumber}`}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selectedBankId && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-gray-500 hover:text-red-600 hover:bg-red-50 shrink-0"
                            onClick={() => setSelectedBankId(undefined)}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateQuotation;
